// client/src/utils/offlineOverlay.js
//
// A VERBATIM MIRROR of packages/core/src/offline/overlay.js.
//
// The web client cannot import @ameca/core (packages/core is not a workspace),
// and this file is pure policy with no imports, so it is copied rather than
// reimplemented — a second implementation of these rules would drift, and the
// two clients disagreeing about what a queued create means is exactly the class
// of bug offline support cannot afford. Change one, copy to the other.
//
// MAKING QUEUED WRITES VISIBLE IN CACHED READS.
//
// An offline write that vanishes from the screen is worse than one that is
// refused: the user retypes it, and now there are two. So when a write is
// queued, the reads it affects have to show it immediately — a class added
// offline must appear in the class list, an edited fee must show its new
// amount, a deleted row must go.
//
// This file is the POLICY and nothing else: pure functions over plain data,
// no storage and no network. That is what lets the web and the phone share the
// same rules exactly — the two clients have different fetch layers and
// different storage, but a queued create must mean the same thing on both.
//
// THE MAPPING IS BY RESOURCE PATH. `POST /api/classes` affects
// `GET /api/classes?anything`; `PUT /api/classes/5` and `DELETE /api/classes/5`
// affect that same list and the single `GET /api/classes/5`. That rule is
// mechanical, needs no per-endpoint registration, and therefore cannot fall out
// of date as pages are added.

/** Rows created offline carry this so the UI can mark them and the sync can find them. */
export const OFFLINE_ID_PREFIX = 'offline:';

export function isOfflineId(id) {
  return typeof id === 'string' && id.startsWith(OFFLINE_ID_PREFIX);
}

/** Strip the query string and any trailing slash. */
export function pathOnly(url) {
  const s = String(url || '');
  const q = s.indexOf('?');
  const base = q === -1 ? s : s.slice(0, q);
  return base.replace(/\/+$/, '') || '/';
}

/**
 * The collection a path belongs to.
 *
 * `/api/classes/5` -> `/api/classes`, and `/api/classes` -> `/api/classes`.
 * A segment counts as an id when it is all digits or a uuid — a NAMED segment
 * like `/api/classes/summary` is a different endpoint, not a row, and folding
 * it into the collection would make every summary read look like a row.
 */
const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|offline:[\w-]+)$/i;

export function resourceOf(url) {
  const parts = pathOnly(url).split('/').filter(Boolean);
  const out = [];
  for (const p of parts) {
    if (ID_SEGMENT.test(p)) break;
    out.push(p);
  }
  return `/${out.join('/')}`;
}

/** The row id in a path, when the path addresses one row. */
export function idInPath(url) {
  const parts = pathOnly(url).split('/').filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    if (ID_SEGMENT.test(parts[i])) return parts[i];
  }
  return null;
}

/**
 * Where the list lives inside a response body.
 *
 * Responses in this app come back as a bare array, or wrapped under one of a
 * handful of keys. Returning the KEY rather than the array lets a caller put a
 * modified list back in the same shape it arrived in, which is what keeps the
 * pages that read `res.rows` working next to those that read `res.data`.
 */
const LIST_KEYS = ['rows', 'data', 'items', 'results', 'records', 'list'];

export function findList(body) {
  if (Array.isArray(body)) return { key: null, list: body };
  if (!body || typeof body !== 'object') return null;
  for (const k of LIST_KEYS) {
    if (Array.isArray(body[k])) return { key: k, list: body[k] };
  }
  // A single-key wrapper like { classes: [...] } or { students: [...] }.
  const keys = Object.keys(body).filter((k) => Array.isArray(body[k]));
  if (keys.length === 1) return { key: keys[0], list: body[keys[0]] };
  return null;
}

export function withList(body, key, list) {
  if (key === null) return list;
  return { ...body, [key]: list };
}

const idOf = (row) => (row && (row.id ?? row._id ?? row.uuid)) ?? null;
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * A queued write, reduced to what the overlay needs.
 * @typedef {{ method: string, endpoint: string, body: any, outboxId: number }} PendingWrite
 */

/** Which queued writes bear on a given GET. */
export function pendingFor(url, pending = []) {
  const target = resourceOf(url);
  return pending.filter((w) => resourceOf(w.endpoint) === target);
}

/**
 * Apply queued writes to a cached response.
 *
 * Applied OLDEST FIRST, because the queue replays in that order and the screen
 * should show what the server will end up with — an edit that follows a create
 * has to land on the created row, not beside it.
 *
 * Returns the body unchanged when nothing applies, so an untouched response is
 * never needlessly rewritten.
 */
export function applyPending(url, body, pending = []) {
  const writes = pendingFor(url, pending);
  if (writes.length === 0) return body;

  const rowId = idInPath(url);

  // A single-row GET: fold in edits to that row, and report a delete as gone.
  if (rowId) {
    let row = body;
    for (const w of writes) {
      if (!sameId(idInPath(w.endpoint), rowId)) continue;
      const m = String(w.method || 'POST').toUpperCase();
      if (m === 'DELETE') return null;
      if (m === 'PUT' || m === 'PATCH') row = { ...(row || {}), ...(w.body || {}) };
    }
    return row;
  }

  const found = findList(body);
  if (!found) return body;

  let list = [...found.list];
  for (const w of writes) {
    const m = String(w.method || 'POST').toUpperCase();
    const wid = idInPath(w.endpoint);

    if (m === 'POST' && !wid) {
      // A create. The temp id is the outbox id, so the row is stable across
      // re-renders and can be matched to its queue entry.
      list.push({
        ...(w.body || {}),
        id: `${OFFLINE_ID_PREFIX}${w.outboxId}`,
        __pendingSync: true,
      });
      continue;
    }
    if ((m === 'PUT' || m === 'PATCH') && wid) {
      list = list.map((r) => (sameId(idOf(r), wid)
        ? { ...r, ...(w.body || {}), __pendingSync: true }
        : r));
      continue;
    }
    if (m === 'DELETE' && wid) {
      list = list.filter((r) => !sameId(idOf(r), wid));
    }
  }

  return withList(body, found.key, list);
}

/**
 * What to hand back to the caller for a write that was queued rather than sent.
 *
 * Shaped like a normal success so calling code — which almost always reads
 * `res.id` or `res.success` — keeps working without every page learning about
 * the queue. `__queued` is there for the pages that DO want to say so.
 */
export function queuedResponse({ method, endpoint, body, outboxId }) {
  const m = String(method || 'POST').toUpperCase();
  const id = idInPath(endpoint) || `${OFFLINE_ID_PREFIX}${outboxId}`;
  return {
    success: true,
    __queued: true,
    __pendingSync: true,
    outboxId,
    id,
    ...(m === 'DELETE' ? {} : (body && typeof body === 'object' ? body : {})),
  };
}

/**
 * Never touched offline, read or write.
 *
 * Authentication decides who the user IS. A stale answer there is not a stale
 * list, it is the wrong person's session.
 */
const NEVER_OFFLINE = [
  /^\/api\/auth\b/,
  /^\/api\/login\b/,
];

/**
 * Writes that must not be queued, even though reading them offline is fine.
 *
 * PAYMENTS, because telling someone a payment succeeded when it was only
 * written to a queue is a lie the school finds out about at the bank.
 *
 * ATTENDANCE, because a register is not a form — the server re-checks that the
 * date is today and the clock is inside the lesson plus grace, both on arrival.
 * A register captured offline can therefore never be delivered: replaying it
 * returns 409, which the flush reads as "still processing, keep it", so it
 * would sit in the queue forever, never sent and never dropped. Refusing at the
 * moment the teacher taps save is the truthful outcome — they find out while
 * they are still standing in front of the class, when they can still do
 * something about it.
 */
const NEVER_QUEUED = [
  /^\/api\/attendance\b/,
  /^\/api\/.*\/checkout\b/,
  /^\/api\/subscriptions\/checkout\b/,
  /^\/api\/payments\b/,
  /^\/api\/.*\/(initiate|confirm|verify)-payment\b/,
];

/**
 * @param {string} url
 * @param {string} [method] defaults to GET — reads are the permissive case
 */
export function offlineAllowed(url, method = 'GET') {
  const p = pathOnly(url);
  if (NEVER_OFFLINE.some((re) => re.test(p))) return false;
  const m = String(method || 'GET').toUpperCase();
  if (m === 'GET' || m === 'HEAD') return true;
  return !NEVER_QUEUED.some((re) => re.test(p));
}
